import { useQueries, useQuery } from "@tanstack/react-query";

import { Raster } from "@nens/lizard/api-v4";
import { FileDescriptorRead, ScenarioMeta, VectorMeta } from "@nens/rana/api-v1";
import {
  Extent,
  combineExtents,
  convertLizardBoundsToMapExtent,
  makeUnique,
  removeNullUndefined,
} from "@nens/shared-utils";

import { useProjectBounds } from "@/hooks/use-project-bounds";
import { getFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { EXTENT } from "@/utils/schematisation-extents";

import { getAllLayers, getFileByPath, getFiles } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Layer } from "../../../types/publication";

export function useLayerBounds(layer: Layer): { bounds: Extent | null; boundsLoading: boolean } {
  const file = usePublicationStore(getFileByPath(layer.filePath));
  const lizardRasterQuery = useQuery(
    getFileDescriptorViewQuery(
      file?.file.descriptor.dataType === "raster" ? file?.file.descriptor : undefined,
      "lizard-raster"
    )
  );

  return {
    bounds: _getLayerBounds(file?.file.descriptor, lizardRasterQuery.data),
    boundsLoading: lizardRasterQuery.isLoading,
  };
}

export function useMultipleLayerBounds(layers: Layer[]): {
  bounds: Extent | null;
  boundsLoading: boolean;
} {
  const files = usePublicationStore(getFiles);
  const filesForLayers = removeNullUndefined(
    layers.map((layer) => files.find((f) => f.path === layer.filePath))
  );

  const descriptors = makeUnique(
    filesForLayers.map((file) => file.file.descriptor),
    (descriptor: FileDescriptorRead) => descriptor.id
  );

  const queries = useQueries({
    queries: descriptors.map((descriptor) =>
      getFileDescriptorViewQuery(
        descriptor.dataType === "raster" ? descriptor : undefined,
        "lizard-raster"
      )
    ),
  });

  const boundsArray = removeNullUndefined(
    layers.map((layer) => {
      const file = filesForLayers.find((f) => f.path === layer.filePath);
      const descriptorIdx = descriptors.findIndex((d) => d.id === file?.file.descriptor.id);
      const lizardRaster = queries[descriptorIdx]?.data;
      return _getLayerBounds(file?.file.descriptor, lizardRaster);
    })
  );

  const bounds = boundsArray.length > 0 ? combineExtents(boundsArray) : EXTENT;

  return {
    bounds,
    boundsLoading: queries.some((q) => q.isLoading),
  };
}

export function usePublicationBounds(): { bounds: Extent | null; boundsLoading: boolean } {
  const projectBounds = useProjectBounds();
  const layers = usePublicationStore(getAllLayers);
  const layerBounds = useMultipleLayerBounds(layers);

  if ((!layers.length || layerBounds.boundsLoading) && projectBounds) {
    return {
      bounds: projectBounds,
      boundsLoading: false,
    };
  }

  return layerBounds;
}

function _getLayerBounds(
  fileDescriptor: FileDescriptorRead | undefined,
  lizardRaster: Raster | undefined
): Extent | null {
  if (!fileDescriptor) return null;

  if (fileDescriptor.dataType === "raster") {
    if (lizardRaster === undefined) return null;

    // Are in west, east, north, south format
    const lizardBounds = lizardRaster.spatial_bounds;
    return convertLizardBoundsToMapExtent(lizardBounds);
  }

  if (fileDescriptor.dataType === "vector") {
    // These have an 'envelope' field.
    const envelope = ((fileDescriptor.meta as VectorMeta).envelope ?? null) as Extent | null;
    return envelope;
  }

  // Scenarios have both an extent and an envelope, it seems the envelope is more likely to be present.
  if (fileDescriptor.dataType === "scenario") {
    const extent = ((fileDescriptor.meta as ScenarioMeta).envelope ?? null) as Extent | null;
    return extent;
  }

  // For other types, we don't know how to get bounds, so return null.
  return null;
}
